import { Dialog } from "../../../../../common/core-ui/Dialog.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import CourseFieldSelect from "./CourseFieldSelect.tsx";
import styles from "./CoursePickerDialog.module.scss";
import { useReducer, useRef, useState } from "react";
import { clsx } from "clsx";
import { useCoursePickerFetcher } from "./coursePickerLoader.ts";
import MountAfterDelay from "../../../../../common/core-ui/MountWithDelay.tsx";

type FieldId = "terms" | "departments" | "courses" | "sections";
const orderedFields: FieldId[] = [
  "terms",
  "departments",
  "courses",
  "sections",
];
const baseAction = "/search/api/courses";

function reducer(state: string[], action: { type: FieldId; value: string }) {
  const index = orderedFields.indexOf(action.type);
  if (action.value) {
    return [...state.slice(0, index), action.value];
  } else {
    return state.slice(0, index);
  }
}

export default function CoursePickerDialog({
  onRequestClose,
}: {
  onRequestClose: () => void;
}) {
  const intl = useIntl();
  const [data, dispatch] = useReducer(reducer, []);
  const [hasIncompleteFormError, setHasIncompleteFormError] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const termsFetcher = useCoursePickerFetcher();

  const fields: {
    label: string;
    placeholder: string;
    id: FieldId;
    fetcher?: ReturnType<typeof useCoursePickerFetcher>;
  }[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Term" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Term" }),
      id: "terms",
      fetcher: termsFetcher,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Department" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Department" }),
      id: "departments",
    },
    {
      label: intl.formatMessage({ defaultMessage: "Course" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Course" }),
      id: "courses",
    },
    {
      label: intl.formatMessage({ defaultMessage: "Section" }),
      placeholder: intl.formatMessage({ defaultMessage: "Select Section" }),
      id: "sections",
    },
  ];

  const goToCourseMaterials = () => {
    if (data.length !== fields.length) {
      setHasIncompleteFormError(true);
      window.setTimeout(() => {
        errorRef.current?.focus();
      }, 10);
    } else {
      alert("Coming soon!");
      onRequestClose();
    }
  };

  return (
    <Dialog
      darkHeader
      onRequestClose={onRequestClose}
      title={intl.formatMessage({ defaultMessage: "Select Course" })}
      primaryButton={{
        label: intl.formatMessage({ defaultMessage: "View Course Materials" }),
        onClick: goToCourseMaterials,
      }}
      secondaryButton={{
        label: intl.formatMessage({ defaultMessage: "Cancel" }),
        onClick: onRequestClose,
      }}
    >
      <FormattedMessage
        tagName="div"
        defaultMessage="Select any course at your institution to view required course materials."
      />
      {termsFetcher.data &&
        (!termsFetcher.data.ok || termsFetcher.data.data?.length === 0) && (
          <div
            className={clsx(
              "vst-validation-box",
              "vst-validation-box-variant-warning",
              styles.error
            )}
            aria-live="polite"
          >
            <MountAfterDelay timeout={10}>
              <FormattedMessage defaultMessage="Course materials information is not available for upcoming terms. Please check back later." />
            </MountAfterDelay>
          </div>
        )}
      {hasIncompleteFormError && (
        <div
          ref={errorRef}
          className={clsx("vst-validation-box", styles.error)}
          tabIndex={-1}
        >
          <FormattedMessage defaultMessage="Please fill out all fields" />
        </div>
      )}
      <div className={styles.selectBlock}>
        {fields.map((field, i) => {
          const action =
            data.length >= i
              ? (baseAction + "/" + data.slice(0, i).join("/")).replace(
                  /\/$/,
                  ""
                )
              : undefined;
          return (
            <CourseFieldSelect
              key={action ?? field.id}
              label={field.label}
              placeholder={field.placeholder}
              onChange={(e) => {
                dispatch({ type: field.id, value: e.target.value });
                if (i === fields.length - 1) {
                  setHasIncompleteFormError(false);
                }
              }}
              value={data[i] ?? ""}
              action={action}
              fetcher={field.fetcher}
            />
          );
        })}
      </div>
    </Dialog>
  );
}
